/** 파일 미리보기 가능한 확장자 목록 */
const PREVIEWABLE_EXTENSIONS = [
  'pdf', 'xlsx', 'xls', 'xlsm', 'xlsb', 'csv', 'docx', 'pptx', 'ppt',
  // 마크다운
  'md', 'mdx', 'markdown',
  // 이미지
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico',
  // 비디오
  'mp4', 'm4v', 'mov', 'webm', 'ogv',
  // 코드 파일
  'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp',
  'cs', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'r',
  'sql', 'html', 'css', 'scss', 'sass', 'less', 'json', 'yaml', 'yml',
  'xml', 'toml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bash', 'zsh',
  'ps1', 'bat', 'cmd', 'dockerfile', 'makefile', 'cmake',
  'lua', 'perl', 'pl', 'dart', 'vue', 'svelte', 'astro',
  'graphql', 'gql', 'proto', 'tf', 'hcl',
  'txt', 'log', 'csv', 'tsv',
];

/** 파일 미리보기 가능한 확장자 확인 */
export function canPreviewFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  // 특수 파일명 처리 (Dockerfile, Makefile 등)
  const baseName = fileName.split('/').pop()?.toLowerCase() || '';
  if (['dockerfile', 'makefile', 'cmakelists.txt'].includes(baseName)) return true;
  return PREVIEWABLE_EXTENSIONS.includes(ext);
}

/** 엑셀 계열 확장자인지 확인 */
export function isExcelFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['xlsx', 'xls', 'xlsm', 'xlsb', 'csv'].includes(ext);
}

/** DOCX 확장자인지 확인 */
export function isDocxFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ext === 'docx';
}

/** PPTX 확장자인지 확인 */
export function isPptxFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['pptx', 'ppt'].includes(ext);
}

/** 마크다운 확장자인지 확인 */
export function isMarkdownFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['md', 'mdx', 'markdown'].includes(ext);
}

/** 이미지 확장자인지 확인 */
export function isImageFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext);
}

/** 브라우저 재생 가능한 비디오 확장자인지 확인 */
export function isVideoFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['mp4', 'm4v', 'mov', 'webm', 'ogv'].includes(ext);
}

/** 브라우저 재생 가능한 오디오 확장자인지 확인 */
export function isAudioFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'webm'].includes(ext);
}

/** HTML 확장자인지 확인 (코드 보기 외에 "렌더링 미리보기" 토글 대상) */
export function isHtmlFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['html', 'htm', 'xhtml'].includes(ext);
}

/** 코드 파일 확장자인지 확인 */
export function isCodeFile(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const baseName = fileName.split('/').pop()?.toLowerCase() || '';
  // 특수 파일명
  if (['dockerfile', 'makefile', 'cmakelists.txt'].includes(baseName)) return true;
  const codeExts = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp',
    'cs', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'r',
    'sql', 'html', 'css', 'scss', 'sass', 'less', 'json', 'yaml', 'yml',
    'xml', 'toml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bash', 'zsh',
    'ps1', 'bat', 'cmd', 'lua', 'perl', 'pl', 'dart', 'vue', 'svelte', 'astro',
    'graphql', 'gql', 'proto', 'tf', 'hcl', 'txt', 'log', 'tsv',
  ];
  return codeExts.includes(ext);
}

/** 확장자 → 구문 강조 언어 매핑 */
export function getLanguageFromFileName(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const baseName = fileName.split('/').pop()?.toLowerCase() || '';

  // 특수 파일명
  if (baseName === 'dockerfile') return 'docker';
  if (baseName === 'makefile' || baseName === 'cmakelists.txt') return 'makefile';

  const map: Record<string, string> = {
    js: 'javascript', jsx: 'jsx', ts: 'typescript', tsx: 'tsx',
    py: 'python', java: 'java', c: 'c', cpp: 'cpp', h: 'c', hpp: 'cpp',
    cs: 'csharp', go: 'go', rs: 'rust', rb: 'ruby', php: 'php',
    swift: 'swift', kt: 'kotlin', scala: 'scala', r: 'r',
    sql: 'sql', html: 'html', css: 'css', scss: 'scss', sass: 'sass', less: 'less',
    json: 'json', yaml: 'yaml', yml: 'yaml', xml: 'xml',
    toml: 'toml', ini: 'ini', cfg: 'ini', conf: 'ini', env: 'bash',
    sh: 'bash', bash: 'bash', zsh: 'bash', ps1: 'powershell', bat: 'batch', cmd: 'batch',
    lua: 'lua', perl: 'perl', pl: 'perl', dart: 'dart',
    vue: 'html', svelte: 'html', astro: 'html',
    graphql: 'graphql', gql: 'graphql', proto: 'protobuf', tf: 'hcl', hcl: 'hcl',
    txt: 'text', log: 'text', tsv: 'text',
  };
  return map[ext] || 'text';
}
